import {
  MathUtils,
  Mesh,
  MirroredRepeatWrapping,
  OrthographicCamera,
  PlaneGeometry,
  RepeatWrapping,
  Scene,
  ShaderMaterial,
  TextureLoader,
  Vector2,
  WebGL1Renderer
} from './texture-three.module.js';

import { snoise } from './texture-lib.js';

// Which gradient images to use
// const gradientPaths = ['//vonholzhausen.com/cdn/shop/t/45/assets/koenigsegg-gamera-texture.png?v=1'];
const gradientPaths = ['//vonholzhausen.com/cdn/shop/t/45/assets/liquidplant-color-story-texture-5.png?v=1'];

const gradientIndex = 0;

// How fast the blobby noise moves; bigger is faster.
const noiseSpeed = 0.16;

// How fast the color cycling moves; bigger is faster.
// const cycleSpeed = 0.02;
const cycleSpeed = 0.04;

// How fast the diagonal stripes move; bigger is faster.
let stripeSpeed = 0;

// The width of the stripes; bigger numbers mean thinner stripes.
// const stripeScale = 5;
const stripeScale = 7;

// Starting offset for the stripes; you'll have to fiddle with the numbers...
// Try increments of 0.1.
const stripeOffset = 0.5;

// We scatter the blobby noise at the pixel level by this amount.
const scatter = 0.0;

// How much should the blob noise push the stripes around?
let waveStrength = 0.1;

// We add a blob from the mouse position; this number affects how strongly.
// Range from 0.0 - 1.0.
// let mouseBlobStrength = 0.55;
let mouseBlobStrength = 0.8;

// Controls how wide the mouse blob is, from right at the mouse at 0.1 to the full window at 1.0.
const mouseBlobRadius = 0.75;

// Affects how strongly the mouse position "tilts" the noise blobs.
// Range from 0.0 - 1.0.
// const mouseTiltStrength = 0;
const mouseTiltStrength = 0.5;

// Controls how quickly the mouse blob catches up to the mouse.
// 0.0 = never, 1.0 = immediately.
const mouseSpring = 0.1;

// We add some "film grain" on top at the end; bigger means stronger.
// Range from 0.0 - 1.0.
const grain = 0.005;

// What percentage of the total gradient makes up the "band" that is seen at any one time.
// Range from 0.0 - 1.0.
// let cycleBandFactor = 1 / 4;
let cycleBandFactor = 1 / 2;

// Fade between gradients?
const useFade = false;

// How long does it take to transition from one gradient to the next? Only valid if useFade is true.
const secondsPerGradient = 5;
const gradientChangeSpeed = 1 / secondsPerGradient;

// Debugging
// stripeSpeed = 0;
// waveStrength = 0;
// mouseBlobStrength = 0;
// cycleBandFactor = 1;

// Variables
const textureLoader = new TextureLoader();
const textures = [];

// ----------
function initTextures() {
  for (let i = 0; i < gradientPaths.length; i++) {
    if (!useFade && i !== gradientIndex) {
      textures.push(null);
    } else {
      const texture = textureLoader.load(gradientPaths[i]);
      texture.wrapS = MirroredRepeatWrapping;
      texture.wrapT = MirroredRepeatWrapping;
      textures.push(texture);
    }
  }
}

// ----------
function getTexture(index) {
  return textures[index];
}

// ----------
function floatString(a) {
  return a.toFixed(6);
}

// ----------
class Waves {
  // ----------
  constructor(container) {
    this.playing = true;
    this.gradientFactor = 0;
    this.gradient0Index = gradientIndex;
    this.gradient1Index = useFade ? (gradientIndex + 1) % gradientPaths.length : gradientIndex;
    this.normalizedMouse = {
      x: 0,
      y: 0
    };

    initTextures();
    const { vertexShader, fragmentShader } = this.initShaders();
    const scene = new Scene();

    const camera = new OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 1, 1000);
    scene.add(camera);

    const renderer = new WebGL1Renderer();
    renderer.setSize(container.clientWidth, container.clientHeight);
    container.appendChild(renderer.domElement);

    const geometry = new PlaneGeometry(1, 1);

    const material = new ShaderMaterial({
      uniforms: {
        time: { value: 1.0 },
        mouse: { value: new Vector2() },
        tilt: { value: new Vector2() },
        resolution: { value: renderer.getSize(new Vector2()) },
        gradient0: { value: getTexture(this.gradient0Index) },
        gradient1: { value: getTexture(this.gradient1Index) },
        gradientFactor: { value: 0 }
      },

      vertexShader,
      fragmentShader
    });

    const mesh = new Mesh(geometry, material);
    scene.add(mesh);

    camera.position.z = 1;

    window.addEventListener('mousemove', (event) => {
      const bounds = container.getBoundingClientRect();
      const x = ((event.clientX - bounds.left) / bounds.width) * 1 - 0.5;
      const y = ((event.clientY - bounds.top) / bounds.height) * -1 + 0.5;
      this.normalizedMouse.x = x;
      this.normalizedMouse.y = y;
    });

    window.addEventListener('resize', () => {
      renderer.setSize(container.clientWidth, container.clientHeight);
      renderer.getSize(material.uniforms.resolution.value);
    });

    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;
    this.material = material;
  }

  // ----------
  initShaders() {
    const vertexShader = `
    precision highp float;
    precision highp int;
  
    // uniform mat4 modelViewMatrix; // optional
    // uniform mat4 projectionMatrix; // optional
    uniform vec2 resolution;
    
    // attribute vec3 position;
    attribute vec4 color;
  
    varying vec3 vPosition;
  
    void main() {
      vPosition = position;

      if (resolution.x < resolution.y) {
        vPosition.x *= resolution.x / resolution.y;
      } else {
        vPosition.y *= resolution.y / resolution.x;
      }

      gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
    }
  `;

    const fragmentShader = `
    precision highp float;
    precision highp int;
  
    uniform float time;
    uniform vec2 mouse;
    uniform vec2 tilt;
    uniform sampler2D gradient0;
    uniform sampler2D gradient1;
    uniform float gradientFactor;
  
    varying vec3 vPosition;

    ${useFade ? '#define useFade 1' : ''}

    const float scatter = ${floatString(scatter)};
    const float grain = ${floatString(grain)};
    const float waveStrength = ${floatString(waveStrength)};
    const float mouseBlobStrength = ${floatString(mouseBlobStrength)};
    const float mouseBlobRadius = ${floatString(mouseBlobRadius)};
    const float noiseSpeed = ${floatString(noiseSpeed)};
    const float cycleSpeed = ${floatString(cycleSpeed)};
    const float stripeSpeed = ${floatString(stripeSpeed)};
    const float stripeOffset = ${floatString(stripeOffset)};
    const float stripeScale = ${floatString(stripeScale)};
    const float cycleBandFactor = ${floatString(cycleBandFactor)};
  
    // From https://thebookofshaders.com/10/
    float random (vec2 st) {
      return fract(sin(dot(st.xy,
        vec2(12.9898,78.233)))*
        43758.5453123
      );
    }

    vec2 random2d(vec2 st) {
      float a = 12.9898;
      float b = 78.233;
      float c = 43758.5453;
      float d = 43758.5453123;
  
      vec2 xy = vec2(dot(st, vec2(a, b)), dot(st, vec2(c, d)));
  
      xy = fract(sin(xy) * 12345.6789);
  
      return xy;
    }
    
    // From https://gist.github.com/983/e170a24ae8eba2cd174f
    vec3 rgb2hsv(vec3 c)
    {
        vec4 K = vec4(0.0, -1.0 / 3.0, 2.0 / 3.0, -1.0);
        vec4 p = mix(vec4(c.bg, K.wz), vec4(c.gb, K.xy), step(c.b, c.g));
        vec4 q = mix(vec4(p.xyw, c.r), vec4(c.r, p.yzx), step(p.x, c.r));

        float d = q.x - min(q.w, q.y);
        float e = 1.0e-10;
        return vec3(abs(q.z + (q.w - q.y) / (6.0 * d + e)), d / (q.x + e), q.x);
    }

    // From https://gist.github.com/983/e170a24ae8eba2cd174f
    vec3 hsv2rgb(vec3 c)
    {
        vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
        vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
        return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
    }
  
    ${snoise}
  
    void main() {
      vec2 pos = vec2(vPosition.x, vPosition.y);
      vec2 tiltedPosition = pos + tilt;

      // Blobs
      vec2 r = random2d(vec2(vPosition.x, vPosition.y)) * scatter;
      float scale = 3.0;
      float noiseX = snoise(vec3(tiltedPosition.x * scale + r.x, tiltedPosition.y * scale + r.y, time * noiseSpeed));
      float noiseY = snoise(vec3(tiltedPosition.x * scale + r.x, tiltedPosition.y * scale + r.y, time * noiseSpeed + 1000.0));

      // Stripe
      float stripe = ((-vPosition.x - noiseX * waveStrength) - (vPosition.y + noiseY * waveStrength));
      stripe += time * stripeSpeed;
      // stripe = pow(stripe, 1.5);
      stripe = sin((stripe - stripeOffset) * stripeScale) * 0.5 + 0.5;
      stripe = 1.0 - stripe;
      float value = stripe;

      // Mouse blob
      float mouseBlob = length(vec2(vPosition.x - mouse.x, vPosition.y - mouse.y));
      mouseBlob = smoothstep(0.0, mouseBlobRadius, mouseBlob);
      value = mix(value, mouseBlob, mouseBlobStrength);

      // Gradient 
      value *= cycleBandFactor;
      value += time * cycleSpeed;
      float rScale = 1000.0;
      float r2 = snoise(vec3(vPosition.x * rScale, vPosition.y * rScale, time * 1.0));
      value = value + (r2 - 0.5) * grain;
      vec2 gradientPos = vec2(value, 0.0);
      
      #ifdef useFade
        vec4 color = mix(texture2D(gradient0, gradientPos), texture2D(gradient1, gradientPos), gradientFactor);
      #else
        vec4 color = texture2D(gradient0, gradientPos);
      #endif

      // Post grain
      // float r2 = random(vec2(vPosition.x + time * 0.001, vPosition.y));

      // float rScale = 1000.0;
      // float r2 = snoise(vec3(vPosition.x * rScale, vPosition.y * rScale, time * 1.0));

      // vec3 hsv = rgb2hsv(color.rgb);
      // hsv.y += (r2 - 0.5) * grain;
      // color.rgb = hsv2rgb(hsv);

      // color = mix(color, vec4(r2, r2, r2, 1.0), grain);

      // Debugging
      // color = vec4(value, value, value, 1.0);
      // color = vec4(random2d(vec2(vPosition.x, vPosition.y)), 0.0, 1.0);
      // color = vec4(vPosition.x, vPosition.y, 0.0, 1.0);
  
      gl_FragColor = color;
    }
  `;

    return { vertexShader, fragmentShader };
  }

  // ----------
  setPlaying(flag) {
    this.playing = flag;
  }

  // ----------
  frame(timeDiffSeconds) {
    const { renderer, scene, camera, material, playing } = this;

    if (!playing) {
      return;
    }

    if (useFade) {
      this.gradientFactor += gradientChangeSpeed * timeDiffSeconds;
      if (this.gradientFactor >= 1) {
        this.gradientFactor = this.gradientFactor % 1;
        this.gradient0Index = this.gradient1Index;
        this.gradient1Index = (this.gradient1Index + 1) % gradientPaths.length;
        material.uniforms.gradient0.value = getTexture(this.gradient0Index);
        material.uniforms.gradient1.value = getTexture(this.gradient1Index);
      }

      material.uniforms.gradientFactor.value = this.gradientFactor;
    }

    if (mouseBlobStrength) {
      material.uniforms.mouse.value.x = MathUtils.lerp(
        material.uniforms.mouse.value.x,
        this.normalizedMouse.x,
        mouseSpring
      );

      material.uniforms.mouse.value.y = MathUtils.lerp(
        material.uniforms.mouse.value.y,
        this.normalizedMouse.y,
        mouseSpring
      );
    }

    if (mouseTiltStrength) {
      material.uniforms.tilt.value.x = MathUtils.lerp(
        material.uniforms.tilt.value.x,
        this.normalizedMouse.x * mouseTiltStrength,
        mouseSpring
      );

      material.uniforms.tilt.value.y = MathUtils.lerp(
        material.uniforms.tilt.value.y,
        this.normalizedMouse.y * mouseTiltStrength,
        mouseSpring
      );
    }

    material.uniforms.time.value += timeDiffSeconds;
    renderer.render(scene, camera);
  }
}

// ----------
function main() {
  const wavePools = [];
  const waveContainers = document.querySelectorAll('.wave-container');
  for (const waveContainer of waveContainers) {
    wavePools.push(new Waves(waveContainer));
  }

  window.setWavePoolPlaying = (index, flag) => {
    const wavePool = wavePools[index];
    if (wavePool) {
      wavePool.setPlaying(flag);
    } else {
      console.error('Bad wavePool index', index);
    }
  };

  let lastTime = null;

  function animate(now) {
    requestAnimationFrame(animate);

    const timeDiffSeconds = lastTime ? (now - lastTime) / 1000 : 0;
    lastTime = now;

    for (const wavePool of wavePools) {
      wavePool.frame(timeDiffSeconds);
    }
  }

  requestAnimationFrame(animate);
}

main();
